import { useEffect, useRef, useState } from "react"
import { Offcanvas } from "react-bootstrap"
import {Alert, Input, Typography, Button, Space, Tabs, Switch, Select} from "antd"
import _ from "lodash"
import secureLocalStorage from "react-secure-storage"
import { toast } from "react-toastify"
import { v4 as uuidv4 } from "uuid"

import SelectOrder from "./SelectOrder"
import TicketDescription from "./TicketDescription"
import CreateTicketFooter from "./CreateTicketFooter"
import { useOrderSearchForTicket } from "./store"
import { useAddEvent } from "modules/add_event/store"
import useAssignedStore from "modules/my_tickets/assigned_tickets/store"
import CategorySelect from "./CategorySelect";
import SubcategorySelect from "./SubcategorySelect";
import moment from "moment";
import { useConsultDoctor } from "modules/dashboards/actionables/store"
import { useTicketSlots } from "./store"
import { SLOT_TYPES, REGIONAL_LANGUAGES } from "./constants"
import { NON_PRIORITY_DUMMY_DOCTOR } from "../dashboards/feedback_form/constants"
import { AGENT_LANGUAGES } from "config"
import devLogger from "helpers/logger"
import { isStickyDoctorRollout } from "../../helpers/utils"
import { getOngoingEngagement } from "./api"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const { Search } = Input
const { Title,Text } = Typography
const ID = secureLocalStorage.getItem("id")

const CreateTicketScreen = ({ customerData, ticketData, source = "header" }) => {
    const { fetchCustomerDetails, customerDetails, crmCategories, fetchCrmCategories } = useOrderSearchForTicket()
    const { addEventData } = useAddEvent()
    const { updateAssignedData } = useAssignedStore()
    const { fetchDoctorSlotData, slotData, stickyDoctorAvailable, stickyDoctorSetting, fetchStickyDoctorSetting } = useConsultDoctor()
    const { ticketSlots, fetchTicketSlotData } = useTicketSlots()

    const [orders, setOrders] = useState([])
    const [shipmentAddress, setShipmentAddress] = useState()
    const [selectedOrder, setSelectedOrder] = useState({})
    const [slotBookingPayload, setSlotBookingPayload] = useState({})
    const [inlineCall, setInlineCall] = useState(false)
    const [validation, setValidation] = useState({})
    const [open, setOpen] = useState(false)
    const [subcategoryOptions, setSubcategoryOptions] = useState([])
    const [selectedCategory, setSelectedCategory] = useState()
    const [escalation, setEscalation] = useState(false)
    const [flowBlocked, setFlowBlocked] = useState(false)
    const [bypassedCombination, setBypassedCombination] = useState(null)
    const [escalationComment, setEscalationComment] = useState('')
    const [escalationLoading, setEscalationLoading] = useState(false)
    const [escalationSuccess, setEscalationSuccess] = useState(false)
    const [escalationSlotMap, setEscalationSlotMap] = useState([])
    const [escalationSelectedSlot, setEscalationSelectedSlot] = useState({})
    const [escalationSlotLoading, setEscalationSlotLoading] = useState(false)
    const [matchedTicketStatus, setMatchedTicketStatus] = useState(null)
    const [escalationInlineTransfer, setEscalationInlineTransfer] = useState(false)
    const [escalationEngagements, setEscalationEngagements] = useState([])
    const [escalationSelectedEngagementId, setEscalationSelectedEngagementId] = useState(null)
    const [escalationAutoSelectedEngagementId, setEscalationAutoSelectedEngagementId] = useState(null)
    const [refreshSlotsTrigger, setRefreshSlotsTrigger] = useState(0)
    const [slotBookingError, setSlotBookingError] = useState(null)
    const [escalationLanguage, setEscalationLanguage] = useState("NA")
    const [escalationStickyDoctor, setEscalationStickyDoctor] = useState(true)
    const autoLangSelectedForSubcat = useRef(null)
    const isStickyDoctorEnabled = stickyDoctorSetting?.isEnable
        ? isStickyDoctorRollout(customerData?.id, customerData?.intervention_doctor, stickyDoctorSetting?.rolloutPercentage)
        : false

    const escalationSessionId = uuidv4()

    const [ticketPayload, setTicketPayload] = useState({
        case_id: null,
        provider: null,
        current_assignment: null,
        author_id: ID,
        action_taker: ID,
        priority: "4",
        category: null,
        sub_category: null,
        is_internal: false,
        products: [],
        total_refund_price: "0.00",
        address: shipmentAddress,
        waba: true,
        remark: "",
        source: "CRM",
        source_meta: {},
        provider_meta: { due_date: null },
        comment: {},
        due_date: null,
    })

    useEffect(() => {
        fetchStickyDoctorSetting()
    }, [])

    useEffect(() => {
        if (open) {
            // Reset form fields when offcanvas is opened
            setSelectedCategory(null)
            setSubcategoryOptions([])
            setSelectedOrder({})
            setValidation({})
            setEscalation(false)
            setFlowBlocked(false)
            setEscalationInlineTransfer(false)
            setEscalationStickyDoctor(true)
            setEscalationLanguage("NA")
            setBypassedCombination(null)
            setEscalationComment('')
            setEscalationSlotMap([])
            setEscalationSelectedSlot({})
            setMatchedTicketStatus(null)
            setSlotBookingError(null)
            setTicketPayload((prevPayload) => ({
                ...prevPayload,
                category: null,
                sub_category: null,
                comment: {},
                remark: "",
            }))

            // Only fetch categories when header create ticket is opened
            if (source === "header") {
                fetchCrmCategories()
            }

            const id = secureLocalStorage.getItem("id")
            const email = secureLocalStorage.getItem("email")
            const roleId = secureLocalStorage.getItem("role")
            const team = secureLocalStorage.getItem("team")
            const designation = secureLocalStorage.getItem("designation")
            const firstName = secureLocalStorage.getItem("first_name")
            const data = {
                event: "create_ticket",
                email: email,
                first_name: firstName,
                user_id: id,
                role_id: roleId,
                team: team,
                designation: designation,
                case_id: customerData?.id,
            }
            addEventData(data)
        }
    }, [open, source, fetchCrmCategories, addEventData])

    useEffect(() => {
        setTicketPayload((prevPayload) => ({ ...prevPayload, case_id: customerData?.id }))
    }, [customerData])

    const setOrderAndAddress = (orders) => {
        setOrders(orders)
        const customerAddress = _.get(orders, [0, "order_meta", "shipping_address"], {})
        setShipmentAddress({
            locality: _.get(customerAddress, "address1", ""),
            landmark: _.get(customerAddress, "address2", ""),
            city: _.get(customerAddress, "city", ""),
            state: _.get(customerAddress, "province", ""),
            pincode: _.get(customerAddress, "zip", ""),
        })
    }

    useEffect(() => {
        setOrderAndAddress(_.get(customerData, "orders", []))
    }, [customerData])

    useEffect(() => {
        if (_.size(customerDetails) > 0) {
            setOrderAndAddress(customerDetails)
        }
    }, [customerDetails])

    useEffect(() => {
        setTicketPayload((prevPayload) => ({ ...prevPayload, address: shipmentAddress }))
    }, [shipmentAddress])

    /**
     * Generic escalation checker - compares ticket by configured fields
     * @param {Object} ticket - Ticket data to compare against
     * @param {Array} fieldCheck - Fields to compare (e.g., ["category", "sub_category"])
     * @returns {boolean} - True if all fields match
     */
    const checkTicketMatch = (ticket, fieldCheck) => {
        if (!fieldCheck || fieldCheck.length === 0) return false;

        return fieldCheck.every((field) => {
            const existingValue = _.get(ticket, field);
            const newValue = _.get(ticketPayload, field);
            return existingValue === newValue;
        });
    };

    /**
     * Maps slot data into grouped structure for Tabs UI
     * @param {Array} slotsInput - Raw slot data from API
     */
    const mapSlotsForEscalation = (slotsInput) => {
        if (!_.isArray(slotsInput) || _.isEmpty(slotsInput)) {
            setEscalationSlotMap([]);
            setEscalationSlotLoading(false);
            return;
        }

        const grouped = slotsInput.reduce((acc, slot) => {
            const date = moment(slot.slotTime).format("DD-MM-YYYY");
            acc[date] = acc[date] || [];
            acc[date].push({
                slotLabel: moment(slot.slotTime).format("hh:mm A"),
                ...slot,
            });
            return acc;
        }, {});


        const mapped = Object.entries(grouped).map(([label, slots]) => {
            const futureSlots = slots.filter((s) => moment(s.slotTime).isAfter(moment()));
            return {
                label,
                key: label,
                children: futureSlots,
            };
        });

        setEscalationSlotMap(mapped);
        setEscalationSlotLoading(false);
    };

    useEffect(() => {
        const { category, sub_category, sub_category_id } = ticketPayload;

        devLogger.log('[ESCALATION] Check started', { category, sub_category });

        // ============ STEP 1: Validate Category Selection ============
        if (!category) {
            devLogger.log('[ESCALATION] No category selected');
            setEscalation(false);
            setEscalationInlineTransfer(false);
            return;
        }

        // ============ STEP 2: Load Escalation Configuration ============
        const currentCatConfig = crmCategories?.find(cat => cat.value === category);

        devLogger.log('[ESCALATION] Category config:', { category, found: !!currentCatConfig });

        if (!currentCatConfig?.escalation_field_check) {
            devLogger.log('[ESCALATION] No escalation_field_check on this category');
            setEscalation(false);
            return;
        }

        const fieldsToCheck = currentCatConfig.escalation_field_check;
        const dateField = currentCatConfig.escalation_date_field || "updatedAt";
        const dayCount = currentCatConfig.escalation_day_count || 15;
        const skipSubcategories = currentCatConfig.escalation_skip_subcategories || [];
        const subcategoryList = currentCatConfig.subcategory || [];

        devLogger.log('[ESCALATION] Config loaded', { fieldsToCheck, dateField, dayCount });

        if (!ticketData || !Array.isArray(ticketData) || ticketData.length === 0) {
            devLogger.log('[ESCALATION] No ticketData available');
            setEscalation(false);
            return;
        }

        // Find matching tickets and filter out skipped subcategories
        const allMatchingTickets = ticketData.filter((ticket) => checkTicketMatch(ticket, fieldsToCheck));
        devLogger.log('[ESCALATION] Matched tickets:', allMatchingTickets.length);

        let matchingTicket = null;
        let skippedCount = 0;
        let selectedIndex = -1;

        for (let idx = 0; idx < allMatchingTickets.length; idx++) {
            const ticket = allMatchingTickets[idx];
            const subcatValue = ticket.sub_category;
            const subcatObject = subcategoryList.find(sub => sub.value === subcatValue);

            // Check if subcategory is in skip list by value (escalation_skip_subcategories stores subcategory values, not _ids)
            const isInSkipList = subcatValue && skipSubcategories.includes(subcatValue);

            // Check if subcategory is not visible in CRM
            const isCrmHidden = subcatObject && subcatObject.crmVisible === false;

            // If subcategory not found in config, skip it (safety measure)
            const isSubcatNotInConfig = !subcatObject && subcatValue;

            const isSkipped = isInSkipList || isCrmHidden || isSubcatNotInConfig;

            if (isSkipped) {
                skippedCount++;
                continue;
            }

            // Allow escalation for both Open and Closed tickets
            // Open → add comment to existing ticket (no slot re-booking)
            // Closed → reopen ticket with slot booking
            if (ticket.status !== 'Closed' && ticket.status !== 'Open') {
                skippedCount++;
                continue;
            }

            matchingTicket = ticket;
            selectedIndex = idx;
            break;
        }

        devLogger.log(`[ESCALATION] Filter result: ${allMatchingTickets.length} matched, ${skippedCount} skipped, selected: ${matchingTicket ? matchingTicket._id : 'none'}`);

        if (!matchingTicket) {
            setEscalation(false);
            return;
        }

        // ============ STEP 4: Check Ticket Age Against Day Window ============
        const ticketDate = _.get(matchingTicket, dateField);

        if (!ticketDate) {
            devLogger.log(`[ESCALATION] No date found for field "${dateField}" on ticket ${matchingTicket._id}`);
            setEscalation(false);
            return;
        }

        const ticketMoment = moment(ticketDate);
        const cutoffDate = moment().subtract(dayCount, 'days').startOf('day');
        const ticketAgeInDays = moment().diff(ticketMoment, 'days');
        const allowEscalation = ticketMoment.isAfter(cutoffDate);

        devLogger.log(`[ESCALATION] Age check: ticket ${matchingTicket._id}, age=${ticketAgeInDays}d, cutoff=${dayCount}d, ${dateField}=${ticketMoment.format('YYYY-MM-DD')}, allow=${allowEscalation}`);

        // Only update if value actually changed
        if (escalation !== allowEscalation) {
            devLogger.log(`[ESCALATION] State change: ${escalation} → ${allowEscalation}`);
            setEscalation(allowEscalation);
        }

        if (allowEscalation) {
            // Only update if status changed
            if (matchedTicketStatus !== matchingTicket.status) {
                setMatchedTicketStatus(matchingTicket.status);
            }

            // Only update sub_category if it actually changed
            if (
                matchingTicket.status === 'Closed' &&
                sub_category !== matchingTicket.sub_category
            ) {
                setTicketPayload((prevPayload) => ({
                    ...prevPayload,
                    sub_category: matchingTicket.sub_category,
                }));
            }
        } else {
            if (matchedTicketStatus !== null) {
                setMatchedTicketStatus(null);
            }
        }

    }, [ticketPayload.category, ticketPayload.sub_category, ticketData, crmCategories]);

    // Fetch slots when escalation is detected, matched ticket is Closed, and subcategory requires slot
    useEffect(() => {
        devLogger.log('[Escalation Slots Debug] Checking slot requirements:', {
            escalationActive: escalation,
            matchedTicketStatus,
            subcategory: ticketPayload.sub_category
        });

        if (!escalation) {
            // Clear slot data when escalation is not active
            devLogger.log('[Escalation Slots Debug] Escalation not active, clearing slots');
            setEscalationSlotMap([]);
            setEscalationSelectedSlot({});
            return;
        }

        // Only show slots if the matched ticket is Closed
        if (matchedTicketStatus !== 'Closed') {
            // No slots needed for Open or other status tickets
            devLogger.log('[Escalation Slots Debug] Matched ticket not Closed, status:', matchedTicketStatus);
            return;
        }

        const { sub_category } = ticketPayload;
        if (!sub_category) {
            devLogger.log('[Escalation Slots Debug] No subcategory selected');
            return;
        }

        // Find subcategory details from subcategoryOptions
        const currentSubcat = subcategoryOptions.find(s => s.value === sub_category);

        // Check if this subcategory requires slot selection
        const hasSlot = currentSubcat?.has_slot;
        const slotType = currentSubcat?.slot_type;

        devLogger.log('[Escalation Slots Debug] Subcategory slot requirements:', {
            subcategoryId: sub_category,
            hasSlot,
            slotType,
            subcategoryLabel: currentSubcat?.label
        });

        if (!hasSlot || !slotType) {
            // No slot required for this subcategory
            return;
        }

        // If ask_language is enabled, wait for language selection before fetching slots
        console.log('[Escalation Language Debug] Slot fetch useEffect:', {
            subcategoryId: sub_category,
            subcategoryName: currentSubcat?.label,
            ask_language: currentSubcat?.ask_language,
            escalationLanguage,
            hasSlot,
            slotType,
        });
        if (currentSubcat?.ask_language) {
            devLogger.log('[Escalation Slots Debug] ask_language enabled - waiting for language selection');
            if (!escalationLanguage) {
                return;
            }

            // Auto-select sticky doctor's language for MEDICAL_ESCALATION — only once per subcategory
            let langToFetch = escalationLanguage;
            if (slotType === SLOT_TYPES.MEDICAL_ESCALATION && autoLangSelectedForSubcat.current !== sub_category) {
                autoLangSelectedForSubcat.current = sub_category
                const doctorLangs = (customerData?.intervention_doctor_language || []).map(l => l.toUpperCase())
                const matchedLang = REGIONAL_LANGUAGES.find(l => doctorLangs.includes(l))
                if (matchedLang) {
                    setEscalationLanguage(matchedLang)
                    langToFetch = matchedLang;
                }
                setEscalationStickyDoctor(Boolean(matchedLang))
            }

            // Fetch slots with language filter
            setEscalationSlotLoading(true);
            if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
                fetchDoctorSlotData(customerData?.id, sub_category, langToFetch, isStickyDoctorEnabled ? escalationStickyDoctor : false);
            } else if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT) {
                fetchTicketSlotData(sub_category, langToFetch);
            }
            return;
        }

        // Fetch slots based on slot type (no language required)
        setEscalationSlotLoading(true);

        if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
            fetchDoctorSlotData(customerData?.id, sub_category, undefined, isStickyDoctorEnabled ? escalationStickyDoctor : false);
        } else if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT) {
            fetchTicketSlotData(sub_category);
        }
    }, [escalation, matchedTicketStatus, ticketPayload.sub_category, subcategoryOptions, customerData?.id, escalationLanguage]);

    // Sync escalation sticky doctor toggle when store fallback triggers
    useEffect(() => {
        if (escalation && stickyDoctorAvailable === false) {
            setEscalationStickyDoctor(false)
        }
    }, [stickyDoctorAvailable])

    // Process slot data when it arrives from stores
    useEffect(() => {
        if (!escalation) {
            return;
        }

        const { sub_category } = ticketPayload;
        if (!sub_category) {
            return;
        }

        const currentSubcat = subcategoryOptions.find(s => s.value === sub_category);
        const slotType = currentSubcat?.slot_type;

        if (slotType === SLOT_TYPES.MEDICAL_ESCALATION && slotData) {
            mapSlotsForEscalation(slotData);
        } else if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT && ticketSlots?.slotsAfter20Min) {
            mapSlotsForEscalation(ticketSlots.slotsAfter20Min);
        } else {
        }
    }, [slotData, ticketSlots, escalation, ticketPayload.sub_category, subcategoryOptions]);

    const onSlotBookingFailed = (errorMessage) => {
        setSlotBookingPayload({});
        setSlotBookingError(errorMessage || 'Slot booking failed. Please select a new slot.');
        setRefreshSlotsTrigger(prev => prev + 1);
    };

    const handleClose = () => {
        setOpen(false);
        setBypassedCombination(null);
        setEscalationComment('');
        setEscalationSuccess(false);
        setEscalationLoading(false);
        setEscalationSlotMap([]);
        setEscalationSelectedSlot({});
        setEscalationSlotLoading(false);
        setEscalationInlineTransfer(false);
        setEscalationLanguage("NA");
        setMatchedTicketStatus(null);
    }
    const handleShow = () => {
        setOpen(true)
    }

    const idInput = (value) => {
        if (value) {
            fetchCustomerDetails(value)
        }
    }

    // Calculate if subcategory should be shown
    const getCategoryConfig = () => {
        const currentCatConfig = crmCategories?.find(cat => cat.value === ticketPayload.category);
        const fieldsToCheck = currentCatConfig?.escalation_field_check;
        const shouldShow = !fieldsToCheck || fieldsToCheck.includes("sub_category");

        // Show subcategory if no escalation config, or if escalation config requires it
        return shouldShow;
    };

    /**
     * Handle slot selection for escalation
     */
    const onEscalationSlotSelection = (slot) => {
        if (_.isEmpty(slot)) {
            return;
        }

        setEscalationSelectedSlot(slot);

        // Check if passed slot
        if (moment(slot.slotTime).isBefore(moment())) {
            toast.error('This slot has passed. Please select a future slot.');
            setEscalationSelectedSlot({});
        }
    };

    const escalateTicket = async () => {
        try {
            // Validate comment is provided
            if (!escalationComment.trim()) {
                toast.error('Please add a comment before escalating');
                return;
            }

            setEscalationLoading(true);

            // Get escalation config from crmCategories store (category level)
            const currentCatConfig = crmCategories?.find(cat => cat.value === ticketPayload.category);

            // If escalation config is not defined, don't escalate
            if (!currentCatConfig?.escalation_field_check) {
                return;
            }

            const fieldsToCheck = currentCatConfig.escalation_field_check;
            const dateField = currentCatConfig.escalation_date_field || "updatedAt";
            const dayCount = currentCatConfig.escalation_day_count || 15;
            const skipSubcategories = currentCatConfig.escalation_skip_subcategories || [];
            const subcategoryList = currentCatConfig.subcategory || [];

            const cutoffDate = moment().subtract(dayCount, 'days').startOf('day');

            const matchingTickets = ticketData.filter((ticket) => {
                const ticketDate = _.get(ticket, dateField);
                if (!moment(ticketDate).isAfter(cutoffDate)) return false;

                // Check fields based on configuration
                if (!checkTicketMatch(ticket, fieldsToCheck)) return false;

                // Apply skip subcategory filter (same logic as escalation check)
                const subcatValue = ticket.sub_category;
                const subcatObject = subcategoryList.find(sub => sub.value === subcatValue);
                const isInSkipList = subcatValue && skipSubcategories.includes(subcatValue);
                const isCrmHidden = subcatObject && subcatObject.crmVisible === false;
                const isSubcatNotInConfig = !subcatObject && subcatValue;
                if (isInSkipList || isCrmHidden || isSubcatNotInConfig) return false;

                return true;
            });

            if (matchingTickets.length === 0) {
                return;
            }

            // Priority 1: Find ticket with Open status
            let existingTicket = matchingTickets.find((ticket) => ticket.status === 'Open');

            // Priority 2: If no Open ticket, get most recently updated using configured date field
            if (!existingTicket) {
                existingTicket = matchingTickets.sort((a, b) => {
                    const dateA = _.get(a, dateField);
                    const dateB = _.get(b, dateField);
                    return moment(dateB).diff(moment(dateA));
                })[0];
            }

            // Build comment object with type, description, and commented_by
            const commentObj = {
                type: "Ticket Escalation",
                description: escalationComment,
                commented_by: ID
            };

            // Check if subcategory requires slot
            const currentSubcat = subcategoryOptions.find(s => s.value === ticketPayload.sub_category);
            const hasSlot = currentSubcat?.has_slot;
            const slotType = currentSubcat?.slot_type;

            // Build payload with required changes and comment
            const escalatePayload = {
                escalation: true,
                comment: commentObj,
                ...(escalationLanguage ? { language: escalationLanguage } : {}),
            };

            // Handle inline transfer or slot booking
            if (hasSlot && slotType) {
                if (escalationInlineTransfer) {
                    // Inline transfer: assign to NON_PRIORITY_DUMMY_DOCTOR and skip slot booking
                    escalatePayload.inline_transfer = true;
                    escalatePayload.current_assignment = NON_PRIORITY_DUMMY_DOCTOR.id;
                    if (escalationSelectedEngagementId) {
                        escalatePayload.inline_parent_engagement_id = escalationSelectedEngagementId;
                    }
                } else if (!_.isEmpty(escalationSelectedSlot)) {
                    // Normal slot booking
                    // Build slotBooking object with required fields
                    const slotBookingPayload = {
                        caseId: customerData?.id,
                        slotTime: escalationSelectedSlot.slotTime,
                        availableSlots: escalationSelectedSlot.slots,
                        isRescheduling: false,
                        orderRelated: false,
                        fromCrm: true,
                    };

                    // Add additional fields for medical escalations
                    if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
                        slotBookingPayload.isMedicalEscalation = true;
                        slotBookingPayload.sessionId = escalationSessionId;
                        slotBookingPayload.is_sticky = escalationStickyDoctor;
                        // Add manual_medical_slot flag for medical escalations
                        escalatePayload.manual_medical_slot = true;
                    }

                    // Add slotBooking and slotType to escalation payload
                    escalatePayload.slotBooking = slotBookingPayload;
                    escalatePayload.slotType = slotType;
                }
            }

            // Only add status fields if ticket is not already Open
            if (existingTicket.status === 'Closed') {
                escalatePayload.status = 'Open';
                escalatePayload.status_code = 2;
            }

            // Call store action and let it complete
            await updateAssignedData(existingTicket._id, escalatePayload);

            // Show success message and mark escalation as successful
            toast.success('Ticket escalated successfully!');
            setEscalationSuccess(true);

            // Close modal after successful escalation
            setTimeout(() => {
                handleClose();
            }, 1500);
        } catch(error) {
            const errorMessage = _.get(
                error,
                ['response', 'data', 'message'],
                error.message || 'Failed to escalate the ticket'
            );
            toast.error(errorMessage);
        } finally {
            setEscalationLoading(false);
        }
    }

    return (
        <>
            {source === "header" ? (
                <button onClick={handleShow} className="topbar-pill topbar-pill--ticket">
                    <span className="topbar-pill__icon">
                        <FontAwesomeIcon icon="ticket-alt" />
                    </span>
                    <span>Create Ticket</span>
                </button>
            ) : (
                <button onClick={handleShow} className="btn btn-outline-primary m-2" style={{ padding: "0.5rem", width: "9rem" }}>
                    Create Ticket
                </button>
            )}

            <Offcanvas show={open} onHide={handleClose} placement={"start"} style={{ width: "41vw", overflowY: "hidden" }}>
                <Offcanvas.Header>
                    <Offcanvas.Title style={{ width: "100%" }}>
                        <div className="d-flex justify-content-between align-items-center" style={{ width: "100%" }}>
                            <Title level={4} className="pb-0 mb-0" style={{ width: "40%" }}>
                                Create Ticket
                            </Title>
                            <Search
                                style={{ width: "60%" }}
                                addonBefore="Search"
                                placeholder="Search orders by order Id/email /phone number"
                                onSearch={idInput}
                                allowClear
                            />
                        </div>
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <div style={{ height: "92%", overflowY: "scroll" }} className="scrollable-container">
                        <SelectOrder
                            orders={orders}
                            setSelectedOrder={setSelectedOrder}
                            setTicketPayload={setTicketPayload}
                            setSlotBookingPayload={setSlotBookingPayload}
                            setValidation={setValidation}
                        />

                        <CategorySelect
                            selectedOrder={selectedOrder}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            setTicketPayload={setTicketPayload}
                            setValidation={setValidation}
                            setSubcategoryOptions={setSubcategoryOptions}
                            setInlineCall={setInlineCall}
                        />

                        {slotBookingError && (
                            <Alert
                                message="Slot Booking Failed"
                                description={slotBookingError}
                                type="error"
                                showIcon
                                closable
                                onClose={() => setSlotBookingError(null)}
                                style={{ marginBottom: 12 }}
                            />
                        )}

                        {(() => {
                            const showSubcategorySelect = !escalation || getCategoryConfig();
                            return showSubcategorySelect && (
                                <SubcategorySelect
                                    selectedOrder={selectedOrder}
                                    selectedCategory={selectedCategory}
                                    subcategoryOptions={subcategoryOptions}
                                    shipmentAddress={shipmentAddress}
                                    setShipmentAddress={setShipmentAddress}
                                    setTicketPayload={setTicketPayload}
                                    setSlotBookingPayload={setSlotBookingPayload}
                                    setValidation={setValidation}
                                    setInlineCall={setInlineCall}
                                    inlineCall={inlineCall}
                                    escalation={escalation}
                                    customerData={customerData}
                                    refreshSlotsTrigger={refreshSlotsTrigger}
                                    onFlowBlockedChange={setFlowBlocked}
                                />
                            );
                        })()}

                        {!escalation && !flowBlocked && (<TicketDescription
                            setTicketPayload={setTicketPayload}
                            ticketPayload={ticketPayload}
                            setValidation={setValidation}
                        />)}

                        {escalation && !escalationSuccess && (
                            <>
                            <Alert
                                message="Ticket Already Exists"
                                type="warning"
                                showIcon
                                description={
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Text>
                                            {matchedTicketStatus === 'Open'
                                                ? 'An open ticket with this category already exists. Please add an escalation comment to the existing ticket.'
                                                : 'A ticket with this category and subcategory already exists (updated within the last 15 days). Please escalate the existing ticket.'
                                            }
                                        </Text>

                                        <div style={{ width: '100%' }}>
                                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                                Escalation Comment <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <Input.TextArea
                                                placeholder="Add a comment explaining the escalation reason..."
                                                value={escalationComment}
                                                onChange={(e) => setEscalationComment(e.target.value)}
                                                rows={4}
                                                disabled={escalationLoading}
                                                style={{ border: '1px solid #D9D9D9' }}
                                            />
                                        </div>

                                        {/* Inline Transfer & Slot Selection - Only show if matched ticket is Closed and subcategory requires slot */}
                                        {(() => {
                                            const currentSubcat = subcategoryOptions.find(s => s.value === ticketPayload.sub_category);
                                            const hasSlot = currentSubcat?.has_slot;
                                            const slotType = currentSubcat?.slot_type;

                                            console.log('[Escalation Language Debug] Rendering check:', {
                                                subcategoryId: ticketPayload.sub_category,
                                                subcategoryName: currentSubcat?.label,
                                                has_slot: hasSlot,
                                                slot_type: slotType,
                                                ask_language: currentSubcat?.ask_language,
                                                matchedTicketStatus,
                                                escalationInlineTransfer,
                                                conditionResults: {
                                                    'hasSlot passes': !!hasSlot,
                                                    'slotType passes': !!slotType,
                                                    'matchedTicketStatus === Closed': matchedTicketStatus === 'Closed',
                                                    'BLOCKS rendering (returns null)': !hasSlot || !slotType || matchedTicketStatus !== 'Closed',
                                                }
                                            });

                                            // Only show slots if matched ticket is Closed AND subcategory has slots configured
                                            if (!hasSlot || !slotType || matchedTicketStatus !== 'Closed') {
                                                return null;
                                            }

                                            return (
                                                <div style={{ width: '100%' }}>
                                                    {/* Language Selection - Only when ask_language is enabled */}
                                                    {currentSubcat?.ask_language && !escalationInlineTransfer && !escalationStickyDoctor && (
                                                        <div style={{ marginBottom: '12px' }}>
                                                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                                                Language <span style={{ color: 'red' }}>*</span>
                                                            </label>
                                                            <Select
                                                                style={{ width: '100%' }}
                                                                placeholder="Select Language"
                                                                value={escalationLanguage}
                                                                onChange={(value) => {
                                                                    setEscalationLanguage(value);
                                                                    setEscalationSlotMap([]);
                                                                    setEscalationSelectedSlot({});
                                                                    const doctorLangs = customerData?.intervention_doctor_language || []
                                                                    const isNASelected = value === 'NA'
                                                                    const isRegionalDoctor = doctorLangs.some(l => REGIONAL_LANGUAGES.includes(l.toUpperCase()))
                                                                    const doctorSpeaksLanguage = doctorLangs.some(l => l.toUpperCase() === value.toUpperCase())
                                                                    let useStickyForLanguage = false
                                                                    if (isStickyDoctorEnabled) {
                                                                        if (isNASelected && isRegionalDoctor) useStickyForLanguage = false
                                                                        else if (!isNASelected && doctorSpeaksLanguage) useStickyForLanguage = true
                                                                        else useStickyForLanguage = false
                                                                    }
                                                                    setEscalationStickyDoctor(useStickyForLanguage);
                                                                }}
                                                                options={Object.entries(AGENT_LANGUAGES).map(([key, value]) => ({
                                                                    label: value,
                                                                    value: key,
                                                                }))}
                                                            />
                                                            {!escalationLanguage && (
                                                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                                                    Please select a language to view available slots
                                                                </Text>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Inline Transfer Toggle - Only for Medical Escalation */}
                                                    {slotType === SLOT_TYPES.MEDICAL_ESCALATION && (
                                                        <>
                                                            <div
                                                                style={{ border: "1px solid #D9D9D9" }}
                                                                className="d-flex justify-content-between align-items-center p-3 my-3 rounded"
                                                            >
                                                                <Text>Inline Transfer</Text>
                                                                <Switch
                                                                    checked={escalationInlineTransfer}
                                                                    onChange={(checked) => {
                                                                        setEscalationInlineTransfer(checked);
                                                                        if (checked) {
                                                                            // Clear slot selection and language when inline transfer is enabled
                                                                            setEscalationSelectedSlot({});
                                                                            setEscalationLanguage("NA");
                                                                            setEscalationSlotMap([]);
                                                                            // Fetch ongoing engagements
                                                                            if (customerData?.id) {
                                                                                getOngoingEngagement(customerData.id).then((res) => {
                                                                                    const data = _.get(res, ['data'], {});
                                                                                    const engList = _.get(data, ['engagements'], []);
                                                                                    setEscalationEngagements(engList);
                                                                                    const autoId = _.get(data, ['auto_selected_engagement_id']);
                                                                                    setEscalationAutoSelectedEngagementId(autoId || null);
                                                                                    const defaultId = autoId && engList.some(e => e._id === autoId)
                                                                                        ? autoId
                                                                                        : _.get(engList, [0, '_id'], null);
                                                                                    setEscalationSelectedEngagementId(defaultId);
                                                                                }).catch(() => {});
                                                                            }
                                                                        } else {
                                                                            setEscalationEngagements([]);
                                                                            setEscalationSelectedEngagementId(null);
                                                                            setEscalationAutoSelectedEngagementId(null);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            {escalationInlineTransfer && escalationEngagements.length > 0 && (
                                                                <Select
                                                                    style={{ width: "100%", marginBottom: 12 }}
                                                                    placeholder="Select Engagement"
                                                                    value={escalationSelectedEngagementId}
                                                                    onChange={(value) => setEscalationSelectedEngagementId(value)}
                                                                    options={escalationEngagements.map((eng) => {
                                                                        const isOnCall = escalationAutoSelectedEngagementId && eng._id === escalationAutoSelectedEngagementId;
                                                                        const statusColor = isOnCall ? '#1890ff' : '#722ed1';
                                                                        return {
                                                                            label: (
                                                                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                                    {eng.tag}
                                                                                    <span style={{
                                                                                        color: statusColor,
                                                                                        fontWeight: 600,
                                                                                        fontSize: 12,
                                                                                        border: `1px solid ${statusColor}`,
                                                                                        borderRadius: 6,
                                                                                        padding: '0px 6px',
                                                                                        marginLeft: 6,
                                                                                    }}>
                                                                                        {isOnCall ? 'On call' : 'Pending'}
                                                                                    </span>
                                                                                </span>
                                                                            ),
                                                                            value: eng._id,
                                                                        };
                                                                    })}
                                                                />
                                                            )}
                                                            {escalationInlineTransfer && (
                                                                <p style={{ color: "red", margin: "0 0 12px 0", fontSize: "14px" }}>
                                                                    Please do not make more than one attempt for inline on Ameyo.
                                                                    If the call doesn't connect, book a slot for the customer.
                                                                </p>
                                                            )}
                                                        </>
                                                    )}

                                                    {/* Doctor Preference Toggle - Only for Medical Escalation when inline transfer is disabled */}
                                                    {isStickyDoctorEnabled && !escalationInlineTransfer && slotType === SLOT_TYPES.MEDICAL_ESCALATION && (
                                                        <div
                                                            style={{ border: "1px solid #D9D9D9" }}
                                                            className="d-flex justify-content-between align-items-center p-3 mb-3 rounded"
                                                        >
                                                            <Text>Doctor Preference: {escalationStickyDoctor ? (customerData?.intervention_doctor_name || 'Same Doctor') : 'All Doctors'}</Text>
                                                            <Switch
                                                                checked={escalationStickyDoctor}
                                                                disabled={!stickyDoctorAvailable || !(customerData?.intervention_doctor_language || []).some(l => l.toUpperCase() === escalationLanguage?.toUpperCase())}
                                                                checkedChildren="Same Doctor"
                                                                unCheckedChildren="All Doctors"
                                                                onChange={(checked) => {
                                                                    setEscalationStickyDoctor(checked)
                                                                    setEscalationSlotMap([])
                                                                    setEscalationSelectedSlot({})
                                                                    const lang = currentSubcat?.ask_language ? escalationLanguage : undefined
                                                                    fetchDoctorSlotData(customerData?.id, ticketPayload.sub_category, lang, checked)
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Slot Selection - Only show if inline transfer is disabled */}
                                                    {!escalationInlineTransfer && (
                                                        <>
                                                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                                                Select Slot <span style={{ color: 'red' }}>*</span>
                                                            </label>

                                                            {escalationSlotLoading ? (
                                                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                                                    <Text>Loading available slots...</Text>
                                                                </div>
                                                            ) : _.isEmpty(escalationSlotMap) ? (
                                                                <Alert
                                                                    message="No slots available"
                                                                    description="There are no available slots for this subcategory. Please try again later."
                                                                    type="info"
                                                                    showIcon
                                                                    style={{ marginBottom: '12px' }}
                                                                />
                                                            ) : (
                                                                        <Tabs
                                                                    defaultActiveKey="1"
                                                                    type="card"
                                                                    size="small"
                                                                    items={escalationSlotMap.map(tab => ({
                                                                        ...tab,
                                                                        children: (
                                                                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                                {tab.children.map((slot) => (
                                                                                    <Button
                                                                                        key={slot.slotTime}
                                                                                        className="m-1"
                                                                                        type={escalationSelectedSlot.slotTime === slot.slotTime ? "primary" : "default"}
                                                                                        disabled={slot.slots?.count === 0}
                                                                                        onClick={() => onEscalationSlotSelection(slot)}
                                                                                    >
                                                                                        {slot.slotLabel}
                                                                                    </Button>
                                                                                ))}
                                                                            </div>
                                                                        )
                                                                    }))}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        <Button
                                            type="primary"
                                            onClick={escalateTicket}
                                            style={{ width: '100%' }}
                                            disabled={
                                                !escalationComment.trim() ||
                                                escalationLoading ||
                                                // Disable if slot is required but not selected AND inline transfer is not enabled
                                                (() => {
                                                    const currentSubcat = subcategoryOptions.find(s => s.value === ticketPayload.sub_category);
                                                    const hasSlot = currentSubcat?.has_slot;
                                                    const isClosed = matchedTicketStatus === 'Closed';
                                                    return hasSlot && isClosed && !escalationInlineTransfer && _.isEmpty(escalationSelectedSlot);
                                                })()
                                            }
                                            loading={escalationLoading}
                                        >
                                            {escalationLoading ? 'Escalating...' : 'Escalate Existing'}
                                        </Button>
                                    </Space>
                                }
                            />
                            </>
                        )}

                        {escalation && escalationSuccess && (
                            <Alert
                                message="Ticket Escalated Successfully"
                                type="success"
                                showIcon
                                description={
                                    <Text>
                                        The ticket has been escalated successfully. The modal will close shortly.
                                    </Text>
                                }
                            />
                        )}
                    </div>

                    {!escalation && (
                        <CreateTicketFooter
                            ticketPayload={ticketPayload}
                            validation={validation}
                            slotBookingPayload={slotBookingPayload}
                            subcategoryOptions={subcategoryOptions}
                            escalation={escalation}
                            bypassedCombination={bypassedCombination}
                            ticketData={ticketData}
                            escalateTicket={escalateTicket}
                            onSlotBookingFailed={onSlotBookingFailed}
                        />
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </>
    )
}

export default CreateTicketScreen
